<template>
  <div class="weather full-width-container" :class="{'summer-weather-overview' : isSummerResort}">
    <div class="flex-grid row">
      <h1 class="title col flex-grow-1 hide-mobile">
        {{ $t("Weather") }}
      </h1>

      <UnitToggles />
    </div>

    <div class="flex-grid row">
      <div class="col current-weather-block">
        <Current :weatherZone="activeWeatherZone" v-if="activeWeatherZone" />
      </div>

      <div class="col snow-depth-block" v-if="isWinterResort">
        <div class="storm-total-text" :class="{'show-metric': measureInMetric}" v-if="stormTotal">
          <div class="storm-total-icon"></div>
          <p>
            <a href="#storm-radar" @click.prevent="toggleStormRadar">
              <span class="ttu">{{ $t("Storm Total") }}:</span> {{ stormTotal }}
            </a>
          </p>
          <StormRadar :embed="snow_report.StormRadar" v-if="showStormRadar" v-on:closeStormModal="closeStormModal"></StormRadar>
        </div>

        <div class="primary-metric-range-text" id="primary-metric-cm-metric" :class="primaryMetricClass">
          <span id="primary-metric-cm-imperial">
            {{ primaryMetricValue }}
            <div class="depth-measurement" :class="{ 'show-metric': measureInMetric, 'hidden': hidePrimaryMetricMeasure }">
              <a href="#primary-metric-cm-metric" class="cm" @click.prevent="toggleSnowUnit('metric')">
                <span tabindex="-1">cm</span>
              </a>
              <a href="#primary-metric-cm-imperial" class="in" @click.prevent="toggleSnowUnit('imperial')">
                <span tabindex="-1">in</span>
              </a>
            </div>
          </span>

        </div>


        <div class="primary-metric-text ttu o-55">{{ primaryMetricType }}</div>
      </div>

      <div class="col conditions-block" v-if="activeWeatherZone">
        <Conditions :currentArea="currentArea" :snowbaseUnit="snowbaseUnit" :snowReport="snow_report" />
      </div>

      <div class="col forecast-snowfall-block">
        <Forecast :forecastDays="forecastDays" />
        <Snowfall :forecastDays="forecastDays"  v-if="isWinterResort" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import UnitToggles from './Weather/UnitToggles.vue';
import Current from './Weather/Current.vue';
import Forecast from './Weather/Forecast.vue';
import Snowfall from './Weather/Snowfall.vue';
import Conditions from './Weather/Conditions.vue';
import StormRadar from './Weather/StormRadar.vue';

export default {
  name: 'Weather',
  data() {
    return {
      showStormRadar: false,
      primaryMetric: null,
    };
  },
  components: {
    Current,
    Forecast,
    Snowfall,
    Conditions,
    StormRadar,
    UnitToggles,
  },
  computed: {
    base() {
      return this.snow_report.BaseArea;
    },
    mid() {
      return this.snow_report.MidMountainArea;
    },
    summit() {
      return this.snow_report.SummitArea;
    },
    currentLocation() {
      if (this.activeWeatherZone) {
        return this.activeWeatherZone.Location;
      }
    },
    currentArea() {
      if (!this.activeWeatherZone) { return; }

      return this[this.currentLocation];
    },
    primaryMetricClass() {
      if (!this.weatherPrimaryLayoutOption) { return; }

      return `primary-${this.weatherPrimaryLayoutOption.split('_').join('-')}-metric`;
    },
    primaryMetricType() {
      if (!this.weatherPrimaryLayoutOption) { return; }
      if (this.weatherPrimaryLayoutOption === '--') { return; }

      switch (this.weatherPrimaryLayoutOption) {
        case 'conditions':
          this.primaryMetric = 'baseConditions';
          return this.$t('Conditions');
          break;
        case 'season_total':
          this.primaryMetric = 'seasonTotal';
          return this.$t('Season Total');
          break;
        case 'toggle_overnight':
          this.primaryMetric = 'sinceLiftsClosed';
          return this.$t('Since Lifts Closed');
          break;
        case 'toggle_24':
          this.primaryMetric = 'snow24Hours';
          return this.$t('24 hour');
          break;
        case 'toggle_48':
          this.primaryMetric = 'snow48Hours';
          return this.$t('48 hour');
          break;
        case 'toggle_72':
          this.primaryMetric = 'snow72Hours';
          return this.$t('72 hour');
          break;
        case 'toggle_7':
          this.primaryMetric = 'snow7Days';
          return this.$t('7 day');
          break;
        case 'toggle_base':
          this.primaryMetric = 'snowBase';
          return this.$t('Snow Base');
          break;
        default:
          return '--';
      }
    },
    primaryMetricValue() {
      return this[this.primaryMetric];
    },
    stormTotal() {
      if (this.snow_report.StormTotalIn === '--') { return; }
      if (this.snow_report.StormTotalIn === '0.0') { return; }
      if (typeof (this.snow_report.StormTotalIn) === 'undefined') { return; }

      return this.snowbaseUnit === 'metric'
        ? `${this.snow_report.StormTotalCM.split('.0')[0]} cm`
        : `${this.snow_report.StormTotalIn.split('.0')[0]} in`;
    },
    forecastDays() {
      if (this.forecast) {
        return [
          this.forecast[this.forecastDaysSettings[0]],
          this.forecast[this.forecastDaysSettings[1]],
          this.forecast[this.forecastDaysSettings[2]],
        ];
      }
      return [];
    },
    hidePrimaryMetricMeasure() {
      if (!this.primaryMetric) { return true; }

      return this.primaryMetric === 'baseConditions';
    },
    measureInMetric() {
      return this.snowbaseUnit == 'metric';
    },
    baseConditions() {
      return this.snow_report.BaseConditions;
    },
    seasonTotal() {
      if (!this.snow_report) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.snow_report.SeasonTotalCm)
        : this.roundValue(this.snow_report.SeasonTotalIn);
    },
    sinceLiftsClosed() {
      if (!this.currentArea) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.SinceLiftsClosedCm)
        : this.roundValue(this.currentArea.SinceLiftsClosedIn);
    },
    snow24Hours() {
      if (!this.currentArea) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.Last24HoursCm)
        : this.roundValue(this.currentArea.Last24HoursIn);
    },
    snow48Hours() {
      if (!this.currentArea) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.Last48HoursCm)
        : this.roundValue(this.currentArea.Last48HoursIn);
    },
    snow72Hours() {
      if (!this.currentArea) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.Last72HoursCm)
        : this.roundValue(this.currentArea.Last72HoursIn);
    },
    snow7Days() {
      if (!this.currentArea) { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.Last7DaysCm)
        : this.roundValue(this.currentArea.Last7DaysIn);
    },
    snowBase() {
      if (!this.activeWeatherZone) { return; }
      if (!this.currentArea) { return; }
      if (this.currentArea.BaseCm === '--' && this.snowbaseUnit === 'metric') { return 0; }
      if (this.currentArea.BaseIn === '--') { return; }

      return this.snowbaseUnit === 'metric'
        ? this.roundValue(this.currentArea.BaseCm.split('.')[0] * 1)
        : this.roundValue(this.currentArea.BaseIn.split('.')[0] * 1);
    },
    ...mapState([
      'activeWeatherZone',
      'weatherZones',
      'snow_report',
      'forecast',
      'snowbaseUnit',
      'forecastDaysSettings',
    ]),
    ...mapGetters([
      'weatherPrimaryLayoutOption',
      'weatherRoundPrimaryValues',
      'isWinterResort',
      'isSummerResort',
    ]),
  },

  methods: {
    toggleStormRadar() {
      if (this.snow_report.StormRadar === '--') { return false; }

      this.showStormRadar = true;
    },
    closeStormModal() {
      this.showStormRadar = false;
    },
    roundValue(val) {
      if (this.weatherRoundPrimaryValues == true) {
        return Math.ceil(val * 1.0);
      }
      return val;
    },
  },
};
</script>

<style lang="scss" scoped>
.weather {
  &.summer-weather-overview {
    min-height: 295px;
  }
  .flex-grid {
    @media screen and (max-width: 1400px) {
      flex-flow: wrap;
      > .col {
        flex-grow: 0;
        &.flex-grow-1 {
          flex-grow: 1;
        }
      }
    }
  }
}

.current-weather-block {
  order: 1;
  max-width: 250px;
  flex-basis: 55%;
  @media (min-width: $mobile) {
    flex-basis: 38%;
  }
  p {
    font-family: $base-font-regular;
    font-size: 14px;
  }
}

.snow-depth-block {
  order: 2;
  position: relative;
  top: 40px;
  @media (min-width: $tablet) {
    top: 50px;
  }
  .primary-metric-range-text {
    color: $dark-gray;
    font-family: $base-font-regular;
    font-size: 38px;
    letter-spacing: -2px;
    line-height: 1.2;
    margin: -10px 0 0 0;
    position: relative;
    @media (min-width: $tablet) {
      font-size: 62px;
    }
    &.primary-conditions-metric {
      font-size: 30px;
      line-height: 1.2;
    }
    span {
      position: relative;
    }
  }
  .primary-metric-text {
    color: $dark-gray;
    font-size: 13px;
    line-height: 1.2;
    margin-left: 0%;
    margin-top: 0;
  }
  .storm-total-text {
    background-color: $alert-bar-background;
    color: $white;
    border-radius: 30px;
    white-space: nowrap;
    padding: 5px 0;
    position: absolute;
    text-align: left;
    left: 0px;
    top: -55px;
    min-width: 150px;
    width: 100%;
    &.show-metric {
      min-width: 165px;
    }
    @media (max-width: 350px) {
      left: -20px;
    }
    @media (min-width: $mobile) {
      left: 0;
      min-width: 200px;
      &.show-metric {
        min-width: 210px;
      }
    }
    @media (min-width: $tablet) {
      top: -50px;
      min-width: 210px;
    }
    @media (min-width: 1300px) {
      top: -90px;
    }
    p {
      font-family: $base-font-bold;
      font-size: 12px;
      font-weight: 600;
      line-height: 1.2;
      margin: 4px 0 -4px;
      span {
        font-family: $base-font-regular;
        font-size: 12px;
        font-weight: 500;
      }
      @media (min-width: $mobile) {
        font-size: 14px;
        span {
          font-size: 15px
        }
      }
    }
    .storm-total-icon {
      background: url('~@/assets/icons/snowflake.svg') no-repeat center center;
      background-size: cover;
      float: left;
      height: 25px;
      margin: -2px 2px 0 0;
      width: 25px;
      @media (min-width: $mobile) {
        height: 35px;
        margin: -5px 2px -5px 0;
        width: 35px;
      }
    }
  }
  .depth-measurement {
    color: $dark-gray;
    font-family: $base-font-regular;
    font-size: 12px;
    letter-spacing: 0;
    position: absolute;
    top: 10px;
    right: -15px;
    &.hidden {
      display: none;
    }
    a {
      display: block;
      &.cm {
        display: none;
      }
    }
    &.show-metric  {
      .in {
        display: none;
      }
      .cm {
        display: block;
      }
    }
  }
}

.conditions-block {
  left: 52%;
  flex-basis: 50%;
  order: 3;
  max-width: 130px;
  padding-right: 0;
  position: relative;
  top: -25px;
  width: 100%;
  @media (min-width: $mobile) {
    left: 0;
    top: 30px;
  }
  @media (min-width: $tablet) {
    flex-basis: 30%;
    max-width: 240px;
    order: 3;
    top: 50px;
  }
  @media screen and (min-width: 1300px) {
    order: 4;
    flex-grow: 0;
    flex-basis: 33%;
    left: 105px;
    top: -50px;
  }
  @media screen and (min-width: 1500px) {
    flex-basis: 36%;
    left: 0;
    order: 3;
    top: 50px;
  }
}

.weather {
  .flex-grid > {
    .forecast-snowfall-block {
      background-color: $body-bg;
      margin-bottom: -20px;
      flex-basis: 120%;
      margin: 0px -20px -20px;
      padding: 20px 0;
      order: 4;
      flex-basis: 120%;
      @media (min-width: $mobile) {
        background-color: transparent;
        margin-bottom: 0;
        margin-top: 20px;
        padding: 0;
        flex-basis: 100%;
      }
      @media screen and (min-width: 1300px) {
        flex-basis: 45%;
        order: 3;
      }
      @media screen and (min-width: 1400px) {
        flex-basis: 55%;
      }
      @media screen and (min-width: 1500px) {
        margin-top: 20px;
        flex-basis: 32%;
        order: 4;
      }
    }
  }
}
</style>

<i18n locale="fr-ca">
{
  "Weather": "MÉTÉO",
  "Storm Total": "Total de la Tempête",
  "24 hour": "24 h",
  "48 hour": "48 h",
  "72 hour": "72 h",
  "7 day": "7 jour",
  "Season Total": "Total saison"
}
</i18n>
